/**
 * The single place network requests are made.
 *
 * Rule (docs/ARCHITECTURE.md §7): no component calls `fetch` directly. Everything
 * goes through here, so auth, base URL and error handling have exactly one
 * definition — and so the Phase 4 identity/hosting change is a module swap rather
 * than a sweep through call sites.
 *
 * The schema is hand-written rather than generated. The backend registers routes via
 * `add_get`/`add_post` with no type annotations, across TWO mechanisms (`server.py`
 * plus `app.router.add_*` inside `wtserver/*.py`). Automatic extraction misses one of
 * them, so it would produce a spec that looks authoritative while omitting the
 * channel core — worse than having no spec. See D6.
 */

import { relayRead, relayRequest } from './relay'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly path: string,
    message: string,
    /** The parsed error body, when there was one — codes like already_exists carry
     *  context (has_agent/cli) that the message string alone cannot. */
    readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** The vault session (`wt_sec`) is absent or stale. Expected, not exceptional. */
  get isVaultLocked(): boolean {
    return this.status === 401
  }
}

/*
 * WHY THE LAST GATED REQUEST WAS REFUSED — the apparatus that answered this was deleted
 * on 2026-09-02, and the knowledge is left here because it will be wanted again.
 *
 * `vault_locked` used to cover three different situations and the screen could only say
 * one thing about them. avengents-server split them (1d1dd51) and still sends the split:
 *
 *   no_session      the vault really is locked
 *   cookie_missing  open on another device, this one sent no unlock cookie
 *   cookie_unknown  open, but this device's unlock expired or was replaced
 *
 * The middle one is what the owner needed all morning and could not be told, because
 * "Vault locked" was a true-looking sentence about the wrong situation.
 *
 * A `lastRefusal` store captured it from every failing response, `getVaultRefusal()` read
 * it, and `probeVaultReason()` deliberately ASKED — because measured, with the vault
 * locked the app issues ZERO gated requests, so a capture-only version had nothing to
 * capture and rendered nothing at all. Its only consumer was security/VaultGate.tsx,
 * which renders nowhere and was deleted with the rest of that screen. What remained was
 * a store written on every refusal and read by nobody, which is the write-only shape this
 * campaign is removing, so it went too rather than waiting for a reader that may not come.
 *
 * FOR WHOEVER BUILDS THE NEXT LOCK SCREEN: the three reasons are on the wire already, in
 * a 401 body as `{error:'vault_locked', reason, message}`. Capture is not enough — ask.
 */

/**
 * Host base URL. Empty string in dev (Vite proxies), configurable for the packaged
 * app. NEVER hardcode a tailnet hostname anywhere else — Phase 4 depends on this
 * being one value (docs/ARCHITECTURE.md §4.1). It now lives in ./origin, which is
 * where the sockets and streams read it from too; when it lived here, only `fetch`
 * obeyed the rule this comment states.
 */

/**
 * CSRF token for mutating requests. Returned as `csrf` by GET /security/status and
 * captured there rather than at each call site — see api.getSecurityStatus.
 *
 * It is required on more than /security/*, and this comment used to say otherwise.
 * Every POST under /project/* needs it too: those handlers go through
 * `chat._require_unlocked`, whose first line is `sec._require_csrf`. That was
 * discovered when avengents-server ran their own new endpoint end-to-end and got a
 * 403 they had not predicted from reading the code — /project/mark_read takes the
 * same arguments as /agent/mark_read but not the same preconditions.
 *
 * Attaching it to EVERY post, not to a path allowlist, is what made that omission
 * cost nothing here. Keep it that way: the guessed scope was wrong once already,
 * and a request that carries an unnecessary header is free while one that omits a
 * necessary one fails as a 403 that the mark-read retry split treats as permanent.
 */
let csrfToken: string | null = null

export function setCsrfToken(token: string | null): void {
  csrfToken = token
}

/**
 * The CSRF header, for mutating requests that cannot go through `api.post`.
 *
 * Uploads use XHR (for progress events) and `/fs/*` uses raw fetch (multipart), so
 * none of them pass through the header block below — and every one was therefore
 * sending no token at all. Browser uploads to `/project/file/upload` have been
 * failing on exactly that: the owner saw "Error: Csrf" and could not attach anything
 * to Project Chat, while agent uploads kept working, because the agent-token branch
 * of that handler returns before ever reaching `_require_csrf`.
 *
 * WHICH IS WHY IT SURVIVED DIAGNOSIS FOR SO LONG. The endpoint tested green from
 * every agent that tried it, so the evidence pointed at storage, at the router, at a
 * stale bundle — everywhere except the one branch only a browser takes. Three agents
 * proved three different things worked, and none of them was the broken path.
 *
 * Same shape as the `httpUrl` miss found the day before: a rule implemented inside
 * one helper reads as a property of the codebase and stays that way until somebody
 * counts the call sites that do not use the helper. That one was inert; this one was
 * live. The difference was luck, not care — so it is attached at EVERY mutating call
 * site rather than to a path allowlist, per the note above.
 *
 * NO LONGER EXPORTED (2026-09-02). It was, for exactly the reason above: call sites
 * outside this file had to reach it. There are none left — the upload XHRs moved onto
 * this layer on 2026-08-07, the two voice fetches on 2026-08-08, and formPost, the last
 * one, went with the dead /fs upload today. csrfCoverage.test.ts now asserts that empty
 * set directly, so a new raw mutation fails there with its file and line, and whoever
 * writes one exports this again as part of that change. Kept module-local meanwhile
 * because the dead-code guard is right that an export nobody imports is a claim the
 * codebase does not support.
 */
function csrfHeader(): Record<string, string> {
  return csrfToken ? { 'X-CSRF-Token': csrfToken } : {}
}

async function request<T>(path: string, init?: RequestInit, device?: string, slowMs?: number): Promise<T> {
  /*
   * THE SAME REQUEST, CARRIED DIFFERENTLY.
   *
   * This used to be `fetch(httpUrl(path))` straight at the machine. The browser
   * cannot reach it any more — no port faces the internet — so the request travels
   * as a command through the router and is served on that machine's own loopback
   * socket, through its own auth middleware and vault gate. Nothing above this
   * line changed: same path strings, same ApiError, same parsed body, so the two
   * thousand lines of api.ts and every screen on top of them are untouched.
   *
   * HEADERS TRAVEL, but only two of them. X-CSRF-Token is required by /security/*
   * mutations, so dropping it would have turned every one of those into a refusal
   * that looked like a server fault; Content-Type says what the body is. Anything
   * else a caller sets stays here, because "forward whatever was set" on a general
   * relay is a door nobody reviewed.
   */
  const method = (init?.method ?? 'GET').toUpperCase()
  let body: unknown
  if (typeof init?.body === 'string') {
    try {
      body = JSON.parse(init.body)
    } catch {
      // Not JSON — nothing here sends anything else, and inventing a shape for it
      // would be worse than the machine refusing something it can name.
      body = undefined
    }
  }

  /*
   * `device` NAMES THE MACHINE, when the caller knows which one.
   *
   * Everything here used to go to whichever machine the relay was pointed at, which
   * is right while a person is looking at one. It stops being right the moment the
   * account has two: the sidebar has to ask EVERY paired machine what it is running,
   * and "the current one" is not an answer any of those questions has.
   *
   * Optional, so the two thousand lines above are unchanged — they are all about the
   * machine in front of you, and that is still the default.
   */
  /*
   * GETs go through relayRead — short deadline, one resubmit — because the relayed
   * answer has no store behind it and a lost stream frame otherwise costs the full
   * 35s ceiling with the screen frozen. The owner measured that cost after every
   * create. Mutations stay on the single long wait: a create that "did not answer"
   * may well have run, and asking again would run it twice.
   */
  let answer =
    method === 'GET' && body === undefined && slowMs
      ? // A read the machine is known to take a while over (api.getSlow): one wait, as long as the caller said.
        await relayRequest('GET', path, undefined, undefined, device ?? undefined, slowMs)
      : method === 'GET' && body === undefined
        ? await relayRead(path, device ?? undefined)
        : await relayRequest(method, path, body, init?.headers, device)

  /*
   * A `csrf` refusal HEALS ITSELF — owner, 2026-08-11: 這個 leave project 的
   * error 修一下 (screenshot: ApiError: csrf under the leave button).
   *
   * The token here is ONE module variable; the account is N machines, each edge
   * minting its own. Boot fetches /security/status from whichever machine the
   * app opened on, so the first mutation aimed at a DIFFERENT machine carries
   * that machine's wrong token and is refused — and the refusal used to be
   * handed to the person as "reload and try again", which re-ran the same boot
   * against the same machine they were looking at, which is why reloading even
   * worked. The transport can do exactly that re-sync itself: ask THE MACHINE
   * THAT REFUSED for its own token, and repeat the call once.
   *
   * Safe to repeat precisely BECAUSE it was refused: the csrf gate is the first
   * check, so a refused mutation never ran — unlike a timeout, where repeating
   * could run it twice (the warning a few lines up). One retry, no loop: a
   * second refusal means something else is wrong and deserves to surface.
   */
  const refusedCsrf =
    answer.status === 403 &&
    !!answer.body &&
    typeof answer.body === 'object' &&
    (answer.body as { error?: unknown }).error === 'csrf'
  if (refusedCsrf && method !== 'GET') {
    const st = await relayRead('/security/status', device ?? undefined)
    const fresh = (st.body as { csrf?: string } | null)?.csrf
    if (fresh) {
      setCsrfToken(fresh)
      answer = await relayRequest(
        method,
        path,
        body,
        { ...((init?.headers as Record<string, string>) ?? {}), ...csrfHeader() },
        device,
      )
    }
  }

  if (answer.status < 200 || answer.status >= 300) {
    const parsed = answer.body
    let detail = `HTTP ${answer.status}`
    if (parsed && typeof parsed === 'object' && 'error' in parsed) {
      detail = String((parsed as { error: unknown }).error)
    }
    throw new ApiError(answer.status, path, detail, parsed)
  }

  return answer.body as T
}

function query(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) q.set(k, String(v))
  }
  const s = q.toString()
  return s ? `?${s}` : ''
}

export const api = {
  get: <T>(path: string, device?: string) => request<T>(path, undefined, device),
  /**
   * A GET the machine may take seconds over — one attempt, waited for `ms` instead of a read's 4 s × 2. Owner,
   * 2026-11-04: an old bookmark's part of a 1.37 GB conversation took ~10 s to read, and the short reads gave up on it.
   */
  getSlow: <T>(path: string, device: string | undefined, ms: number) => request<T>(path, undefined, device, ms),
  /**
   * `device` for the same reason `get` has one, and it matters more here.
   *
   * A read aimed at the wrong machine shows the wrong answer; a WRITE aimed at
   * the wrong machine happens there. The directory picker browses a machine the
   * caller names, which need not be the one the relay is currently pointed at —
   * so creating a folder from it would otherwise land on whichever machine was
   * last opened, and succeed.
   */
  post: <T>(path: string, body?: unknown, device?: string) =>
    request<T>(
      path,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Required by /security/* mutations; harmless elsewhere.
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      },
      device,
    ),

  /**
   * `after` is the reconciliation cursor. Note it deliberately does NOT mark the
   * conversation as read — background reconciliation must not clear unread badges
   * for messages the user never saw. Opening a conversation (no `after`) still marks
   * read, as before.
   */
  projectHistory: (projectId: string, after?: number) =>
    api.get<{ ok: boolean; cursor: number; messages: unknown[] }>(
      `/project/history${query({ project_id: projectId, after })}`,
    ),

  chatHistory: (peerEmail: string, after?: number, limit?: number) =>
    api.get<{ ok: boolean; cursor: number; messages: unknown[] }>(
      `/chat/history${query({ peer_email: peerEmail, after, limit })}`,
    ),
}
